import { useState } from 'react';

const CTR_COLORS = ['#a78bfa', '#4ade80', '#f59e0b'];
const CTR_RATING = (ctr) => {
  if (ctr >= 0.07) return { label: 'Excellent', color: '#44dd66' };
  if (ctr >= 0.05) return { label: 'Great', color: '#88cc44' };
  if (ctr >= 0.03) return { label: 'Good', color: '#ccaa22' };
  if (ctr >= 0.02) return { label: 'Below Avg', color: '#dd8800' };
  if (ctr >= 0.01) return { label: 'Poor', color: '#dd4400' };
  return { label: 'Terrible', color: '#cc0000' };
};

const ALL_SIGNALS = [
  { key: 'curiosityScore', label: 'Curiosity Gap' },
  { key: 'emotionalScore', label: 'Emotional Pull' },
  { key: 'powerScore', label: 'Power Words' },
  { key: 'urgencyScore', label: 'Urgency' },
  { key: 'personalScore', label: 'Personal Feel' },
  { key: 'numberScore', label: 'Numbers' },
  { key: 'capsScore', label: 'Caps Emphasis' },
  { key: 'negativeFraming', label: 'Loss Aversion' },
  { key: 'narrativeScore', label: 'Narrative' },
  { key: 'specificityScore', label: 'Specificity' },
  { key: 'formatScore', label: 'Format Match' },
  { key: 'hookStrength', label: 'Hook Strength' },
  { key: 'relatability', label: 'Relatability' },
  { key: 'concreteness', label: 'Concreteness' },
  { key: 'storyStrength', label: 'Story Strength' },
];

const getStrengthsAndWeaknesses = (signals) => {
  const scored = ALL_SIGNALS.map(s => ({ ...s, value: signals[s.key] || 0 }));
  scored.sort((a, b) => b.value - a.value);
  const strengths = scored.filter(s => s.value > 0.2).slice(0, 4);
  const weaknesses = scored.filter(s => s.value <= 0.2).slice(-4).reverse();
  return { strengths, weaknesses };
};

/**
 * Reusable simulator results display.
 * Props:
 *   results - array of { title, ctr, ctrLow, ctrHigh, clicks, impressions, consistency, wins, allCtrs, signals, archetypeBreakdown }
 *   aiAnalysis - { analysis: [...], comparison: "..." }
 *   totalRounds - number of rounds
 */
const SimulatorResultsView = ({ results, aiAnalysis, totalRounds = 10 }) => {
  const [expandedCard, setExpandedCard] = useState(null);

  if (!results || results.length === 0) return null;

  const bestIdx = results.reduce((best, r, i) => r.ctr > results[best].ctr ? i : best, 0);

  return (
    <div style={{ animation: 'fade-in-up 0.5s ease' }}>

      {/* Multi-run summary badge */}
      {totalRounds > 1 && (
        <div style={resStyles.multiRunBadge}>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="#a78bfa"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z"/></svg>
          <span>{totalRounds} rounds completed</span>
          <span style={resStyles.compMetaDot} />
          <span>{(totalRounds * 100000 * results.length).toLocaleString()} total impressions</span>
          <span style={resStyles.compMetaDot} />
          <span>Results averaged for reliability</span>
        </div>
      )}

      {/* Comparison table */}
      <div style={resStyles.compTable}>
        <div style={resStyles.compHeader}>
          <span style={resStyles.compHeaderLabel}>Average CTR ({totalRounds} round{totalRounds !== 1 ? 's' : ''})</span>
          {results.length > 1 && (
            <span style={{ fontSize: '11px', color: '#64748b' }}>
              {results.length} titles compared
            </span>
          )}
        </div>
        {results.map((r, i) => {
          const rating = CTR_RATING(r.ctr);
          const isBest = results.length > 1 && i === bestIdx;
          const barMax = Math.max(...results.map(x => x.ctr));
          const barPct = barMax > 0 ? (r.ctr / barMax) * 100 : 0;
          return (
            <div key={i} style={{
              ...resStyles.compRow,
              ...(isBest ? { background: 'rgba(74,222,128,0.04)', borderColor: 'rgba(74,222,128,0.15)' } : {}),
            }}>
              <div style={resStyles.compRowTop}>
                <div style={resStyles.compTitleSide}>
                  <span style={{ ...resStyles.compNum, background: `${CTR_COLORS[i]}22`, color: CTR_COLORS[i] }}>{i + 1}</span>
                  <span style={resStyles.compTitleText}>{r.title}</span>
                  {isBest && results.length > 1 && (
                    <span style={resStyles.compBestTag}>BEST</span>
                  )}
                  {r.wins != null && results.length > 1 && (
                    <span style={{
                      fontSize: '9px', fontWeight: '700', letterSpacing: '0.5px',
                      background: r.wins >= 7 ? 'rgba(74,222,128,0.15)' : r.wins >= 4 ? 'rgba(251,191,36,0.15)' : 'rgba(248,113,113,0.15)',
                      color: r.wins >= 7 ? '#4ade80' : r.wins >= 4 ? '#fbbf24' : '#f87171',
                      padding: '2px 8px', borderRadius: '4px', flexShrink: 0,
                    }}>{r.wins}/{totalRounds} WINS</span>
                  )}
                </div>
                <div style={resStyles.compCtrSide}>
                  <span style={{ ...resStyles.compCtrNum, color: rating.color }}>{(r.ctr * 100).toFixed(2)}%</span>
                  <span style={{ ...resStyles.compRatingTag, background: `${rating.color}18`, color: rating.color }}>{rating.label}</span>
                </div>
              </div>
              {/* Visual bar */}
              <div style={resStyles.compBarTrack}>
                <div style={{
                  height: '100%', borderRadius: '4px',
                  width: `${Math.max(barPct, 2)}%`,
                  background: `linear-gradient(90deg, ${CTR_COLORS[i]}88, ${CTR_COLORS[i]})`,
                  transition: 'width 0.5s ease',
                }} />
              </div>
              {/* Per-run CTR dots */}
              {r.allCtrs && (
                <div style={resStyles.runDotsRow}>
                  {r.allCtrs.map((ctr, ri) => (
                    <div key={ri} style={resStyles.runDot}>
                      <span style={{ fontSize: '9px', color: '#475569' }}>R{ri + 1}</span>
                      <span style={{ fontSize: '11px', fontWeight: '700', fontFamily: 'monospace', color: CTR_COLORS[i] }}>
                        {(ctr * 100).toFixed(1)}%
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <div style={resStyles.compMeta}>
                <span>{r.clicks.toLocaleString()} avg clicks</span>
                <span style={resStyles.compMetaDot} />
                <span>{r.impressions.toLocaleString()} impressions/round</span>
                {r.ctrLow != null && r.ctrHigh != null && (
                  <>
                    <span style={resStyles.compMetaDot} />
                    <span>{(r.ctrLow * 100).toFixed(2)}–{(r.ctrHigh * 100).toFixed(2)}% range</span>
                  </>
                )}
                {r.consistency != null && (
                  <>
                    <span style={resStyles.compMetaDot} />
                    <span style={{ color: r.consistency >= 80 ? '#4ade80' : r.consistency >= 60 ? '#fbbf24' : '#f87171' }}>
                      {r.consistency}% consistent
                    </span>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detailed breakdown per title */}
      {results.map((r, i) => {
        const { strengths, weaknesses } = getStrengthsAndWeaknesses(r.signals || {});
        const isExpanded = expandedCard === i;
        const qualityScore = r.signals?.titleQuality || 0;
        const qualityLabel = qualityScore >= 0.55 ? 'Excellent' : qualityScore >= 0.4 ? 'Great' : qualityScore >= 0.25 ? 'Good' : qualityScore >= 0.15 ? 'Weak' : 'Poor';
        const qualityColor = qualityScore >= 0.55 ? '#44dd66' : qualityScore >= 0.4 ? '#4ade80' : qualityScore >= 0.25 ? '#fbbf24' : qualityScore >= 0.15 ? '#dd8800' : '#f87171';

        return (
          <div key={i} style={{ ...resStyles.detailCard, animation: `fade-in-up 0.5s ease ${0.1 * (i + 1)}s both` }}>
            {/* Detail card header */}
            <div style={resStyles.detailHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                <span style={{ ...resStyles.compNum, background: `${CTR_COLORS[i]}22`, color: CTR_COLORS[i] }}>{i + 1}</span>
                <span style={resStyles.detailTitle}>{r.title}</span>
              </div>
              <div style={resStyles.qualityBadge}>
                <span style={{ fontSize: '14px', fontWeight: '800', color: qualityColor }}>{qualityLabel}</span>
                <span style={{ fontSize: '10px', color: '#64748b', marginTop: '-2px' }}>Quality</span>
              </div>
            </div>

            {/* CTR by Viewer Type */}
            {r.archetypeBreakdown?.length > 0 && (
              <div style={resStyles.archetypeSection}>
                <span style={resStyles.sectionLabel}>CTR by Viewer Type</span>
                <div style={resStyles.archetypeGrid}>
                  {[...r.archetypeBreakdown].sort((a, b) => b.ctr - a.ctr).map(a => {
                    const maxCtr = r.archetypeBreakdown.reduce((m, x) => Math.max(m, x.ctr), 0);
                    const barW = maxCtr > 0 ? (a.ctr / maxCtr) * 100 : 0;
                    return (
                      <div key={a.name} style={resStyles.archetypeItem}>
                        <span style={resStyles.archetypeItemName}>{a.name}</span>
                        <div style={resStyles.archetypeItemBar}>
                          <div style={{
                            height: '100%', borderRadius: '3px',
                            width: `${Math.max(barW, 2)}%`,
                            background: `linear-gradient(90deg, ${CTR_COLORS[i]}55, ${CTR_COLORS[i]})`,
                            transition: 'width 0.4s ease',
                          }} />
                        </div>
                        <span style={resStyles.archetypeItemCtr}>{(a.ctr * 100).toFixed(1)}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Strengths & Weaknesses */}
            <div style={resStyles.swGrid} className="res-sw-grid">
              <div style={resStyles.swColumn}>
                <span style={resStyles.swLabel}>Strengths</span>
                {strengths.length > 0 ? strengths.map(s => (
                  <div key={s.key} style={resStyles.swItem}>
                    <div style={resStyles.swBar}>
                      <div style={{
                        height: '100%', borderRadius: '3px',
                        width: `${s.value * 100}%`,
                        background: s.value > 0.5 ? '#4ade80' : '#86efac',
                        transition: 'width 0.4s ease',
                      }} />
                    </div>
                    <span style={resStyles.swName}>{s.label}</span>
                    <span style={{ ...resStyles.swVal, color: '#4ade80' }}>{Math.round(s.value * 100)}</span>
                  </div>
                )) : (
                  <span style={{ fontSize: '12px', color: '#475569', fontStyle: 'italic' }}>No strong signals detected</span>
                )}
              </div>
              <div style={resStyles.swColumn}>
                <span style={{ ...resStyles.swLabel, color: '#f87171' }}>Weaknesses</span>
                {weaknesses.length > 0 ? weaknesses.map(s => (
                  <div key={s.key} style={resStyles.swItem}>
                    <div style={resStyles.swBar}>
                      <div style={{
                        height: '100%', borderRadius: '3px',
                        width: `${Math.max(s.value * 100, 3)}%`,
                        background: 'rgba(248,113,113,0.5)',
                        transition: 'width 0.4s ease',
                      }} />
                    </div>
                    <span style={resStyles.swName}>{s.label}</span>
                    <span style={{ ...resStyles.swVal, color: '#f87171' }}>{Math.round(s.value * 100)}</span>
                  </div>
                )) : (
                  <span style={{ fontSize: '12px', color: '#475569', fontStyle: 'italic' }}>No weak areas</span>
                )}
              </div>
            </div>

            {/* AI Analysis */}
            {aiAnalysis?.analysis?.[i] && (
              <div style={resStyles.aiSection}>
                <span style={resStyles.sectionLabel}>AI Analysis</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {aiAnalysis.analysis[i].strengths?.length > 0 && (
                    <div>
                      <span style={{ fontSize: '11px', fontWeight: '600', color: '#4ade80', marginBottom: '4px', display: 'block' }}>Strengths</span>
                      {aiAnalysis.analysis[i].strengths.map((s, si) => (
                        <p key={si} style={{ fontSize: '12px', color: '#cbd5e1', margin: '2px 0', lineHeight: '1.5', paddingLeft: '8px', borderLeft: '2px solid rgba(74,222,128,0.3)' }}>{s}</p>
                      ))}
                    </div>
                  )}
                  {aiAnalysis.analysis[i].weaknesses?.length > 0 && (
                    <div>
                      <span style={{ fontSize: '11px', fontWeight: '600', color: '#f87171', marginBottom: '4px', display: 'block' }}>Weaknesses</span>
                      {aiAnalysis.analysis[i].weaknesses.map((w, wi) => (
                        <p key={wi} style={{ fontSize: '12px', color: '#cbd5e1', margin: '2px 0', lineHeight: '1.5', paddingLeft: '8px', borderLeft: '2px solid rgba(248,113,113,0.3)' }}>{w}</p>
                      ))}
                    </div>
                  )}
                  {aiAnalysis.analysis[i].archetype_insight && (
                    <div>
                      <span style={{ fontSize: '11px', fontWeight: '600', color: '#a78bfa', marginBottom: '4px', display: 'block' }}>Viewer Insight</span>
                      <p style={{ fontSize: '12px', color: '#94a3b8', margin: '2px 0', lineHeight: '1.5', fontStyle: 'italic' }}>{aiAnalysis.analysis[i].archetype_insight}</p>
                    </div>
                  )}
                  {aiAnalysis.analysis[i].rewrite && (
                    <div style={{ background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.15)', borderRadius: '8px', padding: '10px 12px' }}>
                      <span style={{ fontSize: '10px', fontWeight: '600', color: '#a78bfa', letterSpacing: '0.5px', display: 'block', marginBottom: '4px' }}>SUGGESTED REWRITE</span>
                      <p style={{ fontSize: '13px', color: '#e2e8f0', margin: 0, fontWeight: '500' }}>{aiAnalysis.analysis[i].rewrite}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Expand toggle */}
            <button
              onClick={() => setExpandedCard(isExpanded ? null : i)}
              style={resStyles.expandBtn}
            >
              <span>{isExpanded ? 'Hide' : 'Show'} all signals</span>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="#64748b"
                style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z"/>
              </svg>
            </button>

            {/* Expanded content — all signals */}
            {isExpanded && (
              <div style={resStyles.expandedContent}>
                <div style={resStyles.allSignalsSection}>
                  <span style={resStyles.sectionLabel}>All Signals</span>
                  <div style={resStyles.signalGrid} className="res-signal-grid">
                    {ALL_SIGNALS.map(s => {
                      const val = (r.signals || {})[s.key] || 0;
                      return (
                        <div key={s.key} style={resStyles.signalItem}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={resStyles.signalItemName}>{s.label}</span>
                            <span style={{
                              ...resStyles.signalItemVal,
                              color: val > 0.5 ? '#4ade80' : val > 0.2 ? '#fbbf24' : '#475569',
                            }}>{Math.round(val * 100)}</span>
                          </div>
                          <div style={resStyles.signalItemTrack}>
                            <div style={{
                              height: '100%', borderRadius: '2px',
                              width: `${val * 100}%`,
                              background: val > 0.5 ? '#4ade80' : val > 0.2 ? '#fbbf24' : '#334155',
                              transition: 'width 0.3s ease',
                            }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* AI comparison (multiple titles) */}
      {aiAnalysis?.comparison && results.length > 1 && (
        <div style={{ background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.12)', borderRadius: '10px', padding: '14px 18px', marginTop: '8px' }}>
          <span style={{ fontSize: '11px', fontWeight: '600', color: '#a78bfa', letterSpacing: '0.5px' }}>AI COMPARISON</span>
          <p style={{ fontSize: '13px', color: '#cbd5e1', margin: '6px 0 0', lineHeight: '1.6' }}>{aiAnalysis.comparison}</p>
        </div>
      )}

      <p style={{ fontSize: '11px', color: '#475569', textAlign: 'center', margin: '20px 0 0', lineHeight: '1.5' }}>
        Each simulation runs {totalRounds} independent rounds of 100K viewers. Results are averaged for reliability. Best for relative comparison between titles.
      </p>
    </div>
  );
};

const resStyles = {
  multiRunBadge: {
    display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
    padding: '10px 16px', marginBottom: '12px', borderRadius: '10px',
    background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.12)',
    fontSize: '12px', color: '#a78bfa', fontWeight: '500',
  },
  runDotsRow: {
    display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px',
    padding: '8px 0',
  },
  runDot: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
    padding: '4px 6px', borderRadius: '6px',
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)',
    minWidth: '42px',
  },
  compTable: {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '14px',
    overflow: 'hidden',
    marginBottom: '20px',
  },
  compHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  compHeaderLabel: {
    fontSize: '14px', fontWeight: '700', color: '#e2e8f0',
  },
  compRow: {
    padding: '16px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    transition: 'background 0.2s ease',
  },
  compRowTop: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: '12px', marginBottom: '8px', flexWrap: 'wrap',
  },
  compTitleSide: {
    display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0,
  },
  compNum: {
    width: '22px', height: '22px', borderRadius: '6px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '11px', fontWeight: '700', flexShrink: 0,
  },
  compTitleText: {
    fontSize: '13px', color: '#cbd5e1', fontWeight: '500',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  compBestTag: {
    fontSize: '9px', fontWeight: '700', color: '#4ade80', letterSpacing: '0.5px',
    background: 'rgba(74,222,128,0.15)', padding: '2px 8px', borderRadius: '4px',
    flexShrink: 0,
  },
  compCtrSide: {
    display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0,
  },
  compCtrNum: {
    fontSize: '28px', fontWeight: '800', letterSpacing: '-1px', lineHeight: 1,
  },
  compRatingTag: {
    fontSize: '11px', fontWeight: '600', padding: '3px 8px', borderRadius: '4px',
  },
  compBarTrack: {
    height: '6px', borderRadius: '3px',
    background: 'rgba(255,255,255,0.06)', overflow: 'hidden',
    marginBottom: '6px',
  },
  compMeta: {
    display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap',
    fontSize: '11px', color: '#64748b', fontFamily: 'monospace',
  },
  compMetaDot: {
    width: '3px', height: '3px', borderRadius: '50%', background: '#334155', flexShrink: 0,
  },
  detailCard: {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: '14px',
    padding: '20px 24px',
    marginBottom: '12px',
  },
  detailHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: '16px', marginBottom: '18px',
  },
  detailTitle: {
    fontSize: '14px', fontWeight: '600', color: '#e2e8f0',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  qualityBadge: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '8px 14px', borderRadius: '10px',
    background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.15)',
    flexShrink: 0,
  },
  archetypeSection: {
    marginBottom: '20px',
    padding: '16px',
    background: 'rgba(255,255,255,0.02)',
    borderRadius: '10px',
    border: '1px solid rgba(255,255,255,0.05)',
  },
  swGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px',
  },
  swColumn: {
    display: 'flex', flexDirection: 'column', gap: '6px',
  },
  swLabel: {
    fontSize: '11px', fontWeight: '700', color: '#4ade80',
    textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px',
  },
  swItem: {
    display: 'flex', alignItems: 'center', gap: '8px',
  },
  swBar: {
    width: '40px', height: '6px', borderRadius: '3px',
    background: 'rgba(255,255,255,0.06)', overflow: 'hidden', flexShrink: 0,
  },
  swName: {
    fontSize: '12px', color: '#94a3b8', flex: 1,
  },
  swVal: {
    fontSize: '12px', fontWeight: '700', fontFamily: 'monospace', width: '24px',
    textAlign: 'right', flexShrink: 0,
  },
  aiSection: {
    marginTop: '16px', paddingTop: '16px',
    borderTop: '1px solid rgba(167,139,250,0.12)',
  },
  expandBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
    width: '100%', padding: '10px', marginTop: '16px',
    background: 'none', border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '8px', color: '#64748b', fontSize: '12px',
    cursor: 'pointer', fontFamily: 'inherit', transition: 'border-color 0.2s ease',
  },
  expandedContent: {
    marginTop: '16px', paddingTop: '16px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
  },
  allSignalsSection: {
    marginBottom: '20px',
  },
  sectionLabel: {
    fontSize: '12px', fontWeight: '600', color: '#94a3b8',
    marginBottom: '12px', display: 'block',
  },
  signalGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '10px',
  },
  signalItem: {
    padding: '8px 10px', borderRadius: '8px',
    background: 'rgba(255,255,255,0.02)',
  },
  signalItemName: {
    fontSize: '11px', color: '#94a3b8',
  },
  signalItemVal: {
    fontSize: '12px', fontWeight: '700', fontFamily: 'monospace',
  },
  signalItemTrack: {
    height: '4px', borderRadius: '2px',
    background: 'rgba(255,255,255,0.06)', overflow: 'hidden',
  },
  archetypeGrid: {
    display: 'flex', flexDirection: 'column', gap: '6px',
  },
  archetypeItem: {
    display: 'flex', alignItems: 'center', gap: '10px',
  },
  archetypeItemName: {
    fontSize: '12px', color: '#94a3b8', width: '120px', flexShrink: 0,
  },
  archetypeItemBar: {
    flex: 1, height: '8px', borderRadius: '4px',
    background: 'rgba(255,255,255,0.06)', overflow: 'hidden',
  },
  archetypeItemCtr: {
    fontSize: '12px', color: '#94a3b8', fontFamily: 'monospace',
    width: '48px', textAlign: 'right', flexShrink: 0,
  },
};

export default SimulatorResultsView;
